<template>
  <q-card
    v-ripple="targetIsOpen"
    class="col-auto column full-height noselect"
    :class="{
      'bg-primary': isSelected,
      'text-white': isSelected,
      'cursor-pointer': targetIsOpen
    }"
    @click="onSelectTarget(target)"
  >
    <q-card-section
      v-if="hasDiscriminativeStimulus"
      class="bg-primary-variant text-white"
      :class="{ 'q-py-none': tiny, 'q-py-xs':!tiny }"
    >
      <q-badge
        v-if="!!getTrialsPerTarget(target)"
        color="positive"
        floating
      >
        {{ getTrialsPerTarget(target) }}
      </q-badge>
      {{ discriminativeStimulus }}
    </q-card-section>
    <q-card-section
      class="col row items-center"
      :class="{
        'q-py-xs': (tiny && hasDiscriminativeStimulus),
        'q-py-sm': dense && !tiny,
        'q-py-sm': !dense && !tiny,
      }"
    >
      <q-badge
        v-if="!hasDiscriminativeStimulus && !!getTrialsPerTarget(target)"
        color="positive"
        floating
      >
        {{ getTrialsPerTarget(target) }}
      </q-badge>
      <span>
        <q-chip
          v-if="dense && !tiny"
          size="sm"
          text-color="white"
          :color="getTargetWorkflowProperty('color', '')"
          :icon="getTargetWorkflowProperty('icon', 'help')"
          class="chip-no-label"
        />
      </span>
      {{ target.label }}
    </q-card-section>
    <div
      class="col-auto full-width q-pa-none trial-overview"
    >
      <div class="trial-row">
        <div
          v-for="dataItem in displayData"
          :key="dataItem.key"
          :style="getDataItemStyle(dataItem)"
          class="col text-center"
        >
&nbsp;
        </div>
      </div>
    </div>
    <q-menu
      v-if="targetIsOpen"
      :anchor="promptLevelsMenuPosition"
      transition-show="none"
      transition-hide="none"
      auto-close
      @hide="isSelected = false"
    >
      <q-list style="min-width: 100px">
        <q-item
          v-for="outcome in trialOutcomes"
          :key="outcome.id"
          v-ripple
          clickable
          :style="outcome.style"
          class="prompt-level"
          @click="onTrial(outcome.id)"
        >
          <q-item-section>{{ outcome.label }}</q-item-section>
        </q-item>
        <q-item
          v-if="hasIntervalData"
          v-ripple
          v-close-popup
          clickable
          class="prompt-level"
          @click="onUndo()"
        >
          <q-item-section>{{ $t('common.buttonUndo') }}</q-item-section>
        </q-item>
      </q-list>
    </q-menu>
  </q-card>
</template>
<script>
import {
  chain, concat, find, get, isEmpty, size, slice,
  sumBy, times,
} from 'lodash';
import DataCollection from 'src/mixins/DataCollection';
import Targets from 'src/mixins/Targets';

export default {
  name: 'EsdmData',

  mixins: [Targets, DataCollection],

  emits: ['record', 'record-program-data'],

  data() {
    return {
      isSelected: false,
      intervalPrompt: null,

      trialOutcomes: [
        {
          id: 0,
          value: 1,
          label: '+',
          style: 'color: black; background-color: rgb(102, 255, 102); border-color: rgb(82, 204, 82);',
        },
        {
          id: 1,
          value: 0,
          label: '-',
          style: 'color: black; background-color: rgb(255, 192, 198); border-color: rgb(204, 154, 158);',
        },
      ],
    };
  },

  computed: {

    intervalData() {
      return get(this.data, `intervalData_${get(this.target, 'id', '')}`, []);
    }, // intervalData()

    successfulTrials() {
      return get(this.target, 'successfulTrials', 0);
    }, // numberOfTrials()

    numberOfTrials() {
      return get(this.target, 'numberOfTrials', 0);
    }, // numberOfTrials()

    hasIntervalData() {
      return !isEmpty(this.intervalData);
    }, // hasIntervalData()

    displayData() {
      return chain(this.intervalData)
        .slice(0, this.numberOfTrials)
        .concat(times(this.numberOfTrials - size(this.intervalData), () => (null)))
        .map((itemId, itemIndex) => ({ ...find(this.trialOutcomes, { id: itemId }), key: itemIndex }))
        .value();
    }, // displayData()

    currentIntervalPromptLevel() {
      const id = this.intervalPrompt;
      return chain(this.programPromptLevelTemplate)
        .get('promptLevels')
        .find({ id })
        .value();
    }, // currentIntervalPromptLevel()

    allPositivePromptLevel() {
      return get(this.sessionProgram, ['program', 'allPositivePromptLevel'], null);
    }, // allPositivePromptLevel()

    partialPositivePromptLevel() {
      return get(this.sessionProgram, ['program', 'partialPositivePromptLevel'], null);
    }, // partialPositivePromptLevel()

    allNegativePromptLevel() {
      return get(this.sessionProgram, ['program', 'allNegativePromptLevel'], null);
    }, // allNegativePromptLevel()

    promptLevelsMenuPosition() {
      if (this.$q.platform.is.ipad) {
        return 'bottom left';
      }

      return this.$q.screen.gt.sm ? 'top right' : undefined;
    }, // promptLevelsMenuPosition()

  },

  watch: {
    'data.currentInterval': {
      deep: true,
      immediate: false,
      handler(newInterval, oldInterval) {
        if (!oldInterval || !newInterval || (oldInterval === newInterval)) {
          return;
        }
        const data = this.recordData(oldInterval);
        if (!isEmpty(data)) {
          this.$emit('record', this.sessionProgram, this.target, data);
          this.$emit('record-program-data', this.sessionProgram, { [`intervalData_${this.target.id}`]: [] });
        }

        this.intervalPrompt = null;
      },
    },
  },

  methods: {
    /**
     * Called by parent to signal session finish
     */
    finishSession() {
      // ESDM programs record last interval
      const data = this.recordData(this.data.currentInterval);
      if (!isEmpty(data)) {
        return [{
          target: this.target.id,
          ...data,
        }];
      }
    }, // finishSession()

    onSelectTarget() {
      if (!this.targetIsOpen) {
        return;
      }

      this.notifyDeletedPromptLevelTemplate();

      this.isSelected = true;
    }, // onSelectTarget()

    onTrial(outcomeId) {
      if (!this.targetIsOpen) {
        return;
      }
      if (size(this.intervalData) >= this.numberOfTrials) {
        return;
      }

      const intervalData = concat(this.intervalData, outcomeId);
      this.$emit('record-program-data', this.sessionProgram, { [`intervalData_${this.target.id}`]: intervalData });
    }, // onTrial()

    onUndo() {
      if (this.hasIntervalData) {
        // this.intervalData.pop();
        const intervalData = slice(this.intervalData, 0, -1);
        this.$emit('record-program-data', this.sessionProgram, { [`intervalData_${this.target.id}`]: intervalData });
      }
    }, // onUndo()

    getTrialsPerTarget() {
      const trials = chain(this.data)
        .get('data')
        .filter({ target: get(this.target, 'id') })
        .value();

      const trialCount = size(trials);

      if (trialCount === 0) {
        return '';
      }

      return this.calculateTrialsSummary(trials);
    }, // getTrialsPerTarget()

    recordData(currentInterval) {
      this.analyzeIntervalData();

      if (!this.currentIntervalPromptLevel) {
        return;
      }
      return {
        promptLevel: this.currentIntervalPromptLevel.id,
        scoreValue: this.currentIntervalPromptLevel.score,
        trial: currentInterval,
      };
    }, // recordData()

    analyzeIntervalData() {
      this.intervalPrompt = null;
      if (isEmpty(this.intervalData)) {
        return;
      }
      const successes = sumBy(this.displayData, 'value');
      if (successes === 0) {
        this.intervalPrompt = this.allNegativePromptLevel || null;
      } else if (successes >= this.successfulTrials) {
        this.intervalPrompt = this.allPositivePromptLevel || null;
      } else {
        this.intervalPrompt = this.partialPositivePromptLevel || null;
      }
    }, // analyzeIntervalData()

    getDataItemStyle(dataItem) {
      return get(dataItem, 'style', '');
    }, // getDataItemStyle()

  },

};
</script>
<style scoped>

.trial-overview {
    display: table;
    border-left-style: hidden;
    border-right-style: hidden;
    border-bottom-style: hidden;
    border-collapse:collapse;
}
.trial-overview .trial-row {
    display:table-row;
    width:100%;
}
.trial-overview .trial-row div {
    display: table-cell;
    border: solid lightgrey;
    border-width: 1px 1px 0px 1px;
    height:10px;
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
}
.prompt-level {
  border-width: 0px 5px;
  border-style: solid;
}

.program-overline {
  font-size: 7px;
  line-height:1rem;
}
</style>
